import React, { useEffect, useRef, useState } from 'react';
import { DownloadIcon, PrinterIcon, XIcon } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button } from '../../components/ui/Button';
import { Encounter, Patient, User } from '../../types';
type SickNoteFormProps = {
  encounter: Encounter;
  patient: Patient;
  clinician: User | null;
  onClose: () => void;
};
const TITLE_OPTIONS = ['Mr.', 'Mrs.', 'Miss'] as const;
export function SickNoteForm({
  encounter,
  patient,
  clinician,
  onClose
}: SickNoteFormProps) {
  const printableRef = useRef<HTMLElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [recipient, setRecipient] = useState('');
  const [station, setStation] = useState('');
  const [issueDate, setIssueDate] = useState(toDateInput(new Date()));
  const [title, setTitle] = useState<string>(defaultTitle(patient.sex));
  const [patientName, setPatientName] = useState(`${patient.firstName} ${patient.lastName}`);
  const [declaredDate, setDeclaredDate] = useState(toDateInput(new Date(encounter.startedAt)));
  const [reason, setReason] = useState(defaultReason(encounter));
  const [days, setDays] = useState('');
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  const handlePrint = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    window.setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current) return;
    setIsGeneratingPdf(true);
    setPdfError('');
    try {
      const filename = `BMED_SickNote_${sanitizeFilename(patient.bmcFileNumber)}_${sanitizeFilename(patient.lastName)}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (error) {
      console.error('Sick note PDF generation failed', error);
      setPdfError('The sick note PDF could not be generated. Please try again or use Print.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 print:bg-white print:p-0" role="dialog" aria-modal="true" aria-labelledby="sick-note-title" data-id="element-2846" data-mp="1wfwapn35puf8">
      <div className="test-modal-print max-h-[95vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white shadow-2xl print:max-h-none print:max-w-none print:overflow-visible print:rounded-none print:shadow-none" data-id="element-2847" data-mp="13srzh0dh0jv0">
        <header className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white px-5 py-3 print:hidden" data-id="element-2848" data-mp="1djeb6ji2gq8f">
          <div data-id="element-2849" data-mp="l0z8fbhyp8px">
            <h2 id="sick-note-title" className="text-lg font-bold text-gray-900" data-id="element-2850" data-mp="l0hewmmop8lp">
              Certificate of admission to sicklist
            </h2>
            <p className="text-sm text-gray-500" data-id="element-2851" data-mp="2uagrgg2fwx6c">
              Complete the details, then print or download the sick note.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-id="element-2852" data-mp="2pa2ld3kefxay">
            <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-2853" data-mp="2kcsjgoftpt68">
              <PrinterIcon className="mr-1.5 h-4 w-4" data-id="element-2854" data-mp="2ken4s3v0bnau" /> Print
            </Button>
            <Button size="sm" onClick={handleDownloadPdf} disabled={isGeneratingPdf} isLoading={isGeneratingPdf} data-id="element-2855" data-mp="2fehdiosng2ta">
              {!isGeneratingPdf && <DownloadIcon className="mr-1.5 h-4 w-4" data-id="element-2856" data-mp="26irvmpqetqkn" />}
              {isGeneratingPdf ? 'Generating PDF…' : 'Download PDF'}
            </Button>
            <button type="button" onClick={onClose} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label="Close sick note" data-id="element-2857" data-mp="1wmy7sa78qank">
              <XIcon className="h-5 w-5" data-id="element-2858" data-mp="3rs7fzz4czwl7" />
            </button>
          </div>
        </header>

        {pdfError && <p role="alert" className="mx-5 mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 print:hidden" data-id="element-2859" data-mp="3i0bgqkqje0fe">
            {pdfError}
          </p>}

        <section className="grid gap-4 border-b border-gray-200 bg-gray-50 px-5 py-5 sm:grid-cols-2 print:hidden" aria-label="Sick note details" data-id="element-2860" data-mp="38z3pgmmftzut">
          <div className="sm:col-span-2" data-id="element-2861" data-mp="2z1flr76uti0i">
            <label htmlFor="sick-note-recipient" className="mb-1 block text-sm font-medium text-gray-700" data-id="element-2862" data-mp="2z2h5e01xrctu">
              To (head of officer&apos;s department)
            </label>
            <textarea id="sick-note-recipient" rows={2} value={recipient} onChange={event => setRecipient(event.target.value)} placeholder="e.g. The Human Resource Manager, Ministry of Works" className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-2863" data-mp="x7q2eeyc7hw5" />
          </div>
          <div data-id="element-2864" data-mp="2rpj5inzqstpw">
            <label htmlFor="sick-note-station" className="mb-1 block text-sm font-medium text-gray-700" data-id="element-2865" data-mp="2roztfnwrwzjs">
              Station
            </label>
            <input id="sick-note-station" type="text" value={station} onChange={event => setStation(event.target.value)} className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-2866" data-mp="31gm15a9wkghs" />
          </div>
          <div data-id="element-2867" data-mp="1l3felj4iueln">
            <label htmlFor="sick-note-issue-date" className="mb-1 block text-sm font-medium text-gray-700" data-id="element-2868" data-mp="1g2rg6cdyqvhe">
              Date of certificate
            </label>
            <input id="sick-note-issue-date" type="date" value={issueDate} onChange={event => setIssueDate(event.target.value)} className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-2869" data-mp="3l3kgsdqfsxz1" />
          </div>
          <div data-id="element-2870" data-mp="2w4lxtoh17b0">
            <label htmlFor="sick-note-title" className="mb-1 block text-sm font-medium text-gray-700" data-id="element-2871" data-mp="7xbwg0i00kld">
              Title
            </label>
            <select id="sick-note-title" value={title} onChange={event => setTitle(event.target.value)} className="w-full rounded-md border border-gray-300 bg-white p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-2872" data-mp="cqegni0vyhow">
              {TITLE_OPTIONS.map(option => <option key={option} value={option} data-id="element-2873" data-mp="10tbcyu2153o1">
                  {option}
                </option>)}
            </select>
          </div>
          <div data-id="element-2874" data-mp="qt0xt3wfpm68">
            <label htmlFor="sick-note-patient" className="mb-1 block text-sm font-medium text-gray-700" data-id="element-2875" data-mp="qultiwuhjb5o">
              Patient name
            </label>
            <input id="sick-note-patient" type="text" value={patientName} onChange={event => setPatientName(event.target.value)} className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-2876" data-mp="1jihpu0g5711x" />
          </div>
          <div data-id="element-2877" data-mp="228pni312ozaq">
            <label htmlFor="sick-note-declared-date" className="mb-1 block text-sm font-medium text-gray-700" data-id="element-2878" data-mp="22995tkumxppa">
              Declared incapable on
            </label>
            <input id="sick-note-declared-date" type="date" value={declaredDate} onChange={event => setDeclaredDate(event.target.value)} className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-2879" data-mp="2h5ro7r31i86n" />
          </div>
          <div data-id="element-2880" data-mp="2zwslmaoihvo3">
            <label htmlFor="sick-note-days" className="mb-1 block text-sm font-medium text-gray-700" data-id="element-2881" data-mp="2zvzkbp8bnydc">
              Days off duty
            </label>
            <input id="sick-note-days" type="number" min={0} value={days} onChange={event => setDays(event.target.value)} className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-2882" data-mp="2pyuyjqie24x2" />
          </div>
          <div className="sm:col-span-2" data-id="element-2883" data-mp="1jfwstpsoe7j9">
            <label htmlFor="sick-note-reason" className="mb-1 block text-sm font-medium text-gray-700" data-id="element-2884" data-mp="1jl900ed9jiw">
              Owing to (medical reason)
            </label>
            <textarea id="sick-note-reason" rows={2} value={reason} onChange={event => setReason(event.target.value)} className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-2885" data-mp="akb2gxg5mxtg" />
          </div>
        </section>

        <article ref={printableRef} className="bg-white p-8 text-gray-900 sm:p-10 print:p-0" data-id="element-2886" data-mp="3nse66kk7uv93">
          <header className="flex items-center gap-4 border-b-2 border-black pb-4" data-id="element-2887" data-mp="3j032d6mcupb9">
            <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-16 w-16 shrink-0 object-contain" data-id="element-2888" data-mp="2varyvhdmegtf" />
            <div data-id="element-2889" data-mp="3jd6kmwf0ug3g">
              <p className="text-xl font-bold uppercase tracking-wide text-pink-600" data-id="element-2890" data-mp="3jblz1waoq7hq">
                BMED Medical Centre
              </p>
              <p className="mt-0.5 text-sm italic text-gray-600" data-id="element-2891" data-mp="3aciesozs7m1n">
                Caring Healing Leading
              </p>
            </div>
          </header>

          <h1 className="my-7 text-center text-lg font-bold uppercase tracking-[0.12em] underline" data-id="element-2892" data-mp="35g1gj1yvbrau">
            Certificate of Admission to Sicklist
          </h1>

          <div className="grid gap-6 text-sm sm:grid-cols-[1.4fr_1fr]" data-id="element-2893" data-mp="2vnc8kh2j0vk9">
            <div data-id="element-2894" data-mp="2qoj5zjos2oyv">
              <p className="flex items-baseline gap-2" data-id="element-2895" data-mp="2qq2lh82m0vzl">
                <span className="font-semibold" data-id="element-2896" data-mp="2mnfrb0iguyz8">TO:</span>
                <span className="min-w-0 flex-1" data-id="element-2897" data-mp="2mp0kalp4u9uz">{fillLine(recipient)}</span>
              </p>
              <p className="mt-6 text-xs font-semibold uppercase tracking-wide" data-id="element-2898" data-mp="2hn1ivhcydn0o">
                Head of Officer&apos;s Department
              </p>
              <p className="mt-1 text-xs italic text-gray-600" data-id="element-2899" data-mp="s77h6cnq8y18">
                (Copy to Director of Medical Services)
              </p>
            </div>
            <div className="space-y-5" data-id="element-2900" data-mp="x4qw7es91eu8">
              <p className="flex items-baseline gap-2" data-id="element-2901" data-mp="125exque3te2p">
                <span className="font-semibold" data-id="element-2902" data-mp="125wstnp15m91">STATION:</span>
                <span className="min-w-0 flex-1" data-id="element-2903" data-mp="167ib5okc2sqm">{fillLine(station)}</span>
              </p>
              <p className="flex items-baseline gap-2" data-id="element-2904" data-mp="9h9de8qypr3e">
                <span className="font-semibold" data-id="element-2905" data-mp="9ikdnyl78gpr">DATE:</span>
                <span className="min-w-0 flex-1" data-id="element-2906" data-mp="ef11erohkt2b">
                  {fillLine(formatDate(issueDate))}
                </span>
              </p>
            </div>
          </div>

          <hr className="my-6 border-t border-black" data-id="element-2907" data-mp="n7w1z830e0lr" />

          <div className="space-y-5 text-sm leading-8" data-id="element-2908" data-mp="n9fgat53u1kw">
            <p data-id="element-2909" data-mp="3mwffz64cc5lz">
              I have to inform you that{' '}
              <span className="font-semibold" data-id="element-2910" data-mp="3mtt815djfo38">{title}</span>{' '}
              <span className="inline-block min-w-[16rem] border-b border-black px-1 font-semibold" data-id="element-2911" data-mp="3qvwjvv44cup1">
                {patientName || '\u00A0'}
              </span>{' '}
              <span className="text-xs text-gray-600" data-id="element-2912" data-mp="t45xare2z1uh">
                (File No: {patient.bmcFileNumber})
              </span>
            </p>
            <p data-id="element-2913" data-mp="17rr16nis553g">
              has today{' '}
              <span className="inline-block min-w-[12rem] border-b border-black px-1" data-id="element-2914" data-mp="12tpg5x1smvpc">
                {formatDate(declaredDate) || '\u00A0'}
              </span>{' '}
              been declared temporarily incapable of carrying out duties owing
              to{' '}
              <span className="inline-block min-w-[18rem] border-b border-black px-1" data-id="element-2915" data-mp="a6wcp6x0ribn">
                {reason || '\u00A0'}
              </span>{' '}
              and is expected to be off duty for{' '}
              <span className="inline-block min-w-[6rem] border-b border-black px-1 text-center font-semibold" data-id="element-2916" data-mp="k5c65gjir3ob">
                {days || '\u00A0'}
              </span>{' '}
              days.
            </p>
          </div>

          <footer className="mt-16 flex justify-end" data-id="element-2917" data-mp="231xfvaokt2us">
            <div className="w-72" data-id="element-2918" data-mp="230cjdfipb9ua">
              <div className="flex items-baseline gap-2" data-id="element-2919" data-mp="280h1f9z9ccmm">
                <span className="font-semibold" data-id="element-2920" data-mp="2821usw9u5piw">Signed:</span>
                <span className="min-w-0 flex-1 border-b border-black pb-1 text-sm font-medium" data-id="element-2921" data-mp="1t49ise3hzj9g">
                  {clinician?.fullName || '\u00A0'}
                </span>
              </div>
              <p className="mt-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-700" data-id="element-2922" data-mp="2lrlv7vfy2ap1">
                Medical Officer
              </p>
            </div>
          </footer>
        </article>
      </div>
    </div>;
}
function fillLine(value: string) {
  return <span className="block border-b border-black pb-0.5" data-id="element-2923" data-mp="2h1f3hmq4ya2k">
      {value || '\u00A0'}
    </span>;
}
function defaultTitle(sex: string) {
  return sex?.toLowerCase().startsWith('m') ? 'Mr.' : 'Mrs.';
}
function defaultReason(encounter: Encounter) {
  const diagnoses = (encounter.clinicalNote?.workingDiagnosis || []).filter(item => item.trim());
  return diagnoses.join(', ');
}
function toDateInput(date: Date) {
  return Number.isNaN(date.getTime()) ? '' : new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}
function formatDate(value: string) {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}
function sanitizeFilename(value: string) {
  return value.replace(/[^a-z0-9-_]/gi, '_') || 'patient';
}
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const pageWidth = 210;
  const pageHeight = 297;
  const marginTop = 17.78;
  const marginRight = 10.16;
  const marginBottom = 17.78;
  const marginLeft = 12.7;
  const contentWidth = pageWidth - marginLeft - marginRight;
  const contentHeight = pageHeight - marginTop - marginBottom;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const pixelsPerMillimetre = sourceCanvas.width / contentWidth;
  const sliceHeight = Math.floor(contentHeight * pixelsPerMillimetre);
  let cursor = 0;
  let page = 0;
  while (cursor < sourceCanvas.height) {
    const height = Math.min(sliceHeight, sourceCanvas.height - cursor);
    const slice = document.createElement('canvas');
    slice.width = sourceCanvas.width;
    slice.height = height;
    const context = slice.getContext('2d');
    if (!context) break;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, slice.width, slice.height);
    context.drawImage(sourceCanvas, 0, cursor, sourceCanvas.width, height, 0, 0, slice.width, height);
    if (page > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', marginLeft, marginTop, contentWidth, height / pixelsPerMillimetre);
    cursor += height;
    page += 1;
  }
  pdf.save(filename);
}